/**
 * T356 / T357 — IMAP + Drive admin config panel.
 *
 * Two independent sub-forms (IMAP, Drive) + a test button each. Sensitive
 * fields (`imapPassword`, `driveServiceAccountJson`) are write-only — the
 * GET returns `{encrypted: true, setAt}` markers and we render a "set at
 * <date>" hint + a "Replace" affordance. Empty password strings are left
 * unchanged on submit so the user can edit host/user without re-entering
 * the password.
 *
 * The "IMAP testen" button (T357) hits `POST /integrations/imap/test`.
 * In the Phase-1 skeleton the service returns `{attempted:false}` when
 * no credentials are set; after configuration, real imapflow wiring
 * (Phase-11 follow-up) will return the live result. Mock greenmail is
 * only used at the Playwright layer (T358-T361).
 */
import { useState } from 'react';
import { Button, LoadingSpinner } from '../ui';
import { Input } from '../ui/Input';
import { useToastHelpers } from '../ui/Toast';
import {
  useImapConfig,
  useImapTest,
  usePatchImapConfig,
  useDriveConfig,
  useDriveTest,
  usePatchDriveConfig,
} from '../../services/api/integrations';
import { formatDateTimeBerlin } from '../../lib/format-date';

export function IntegrationsSection() {
  return (
    <div className="space-y-8" data-testid="admin-integrations">
      <ImapConfigForm />
      <DriveConfigForm />
    </div>
  );
}

function ImapConfigForm() {
  const { data, isLoading } = useImapConfig();
  const patch = usePatchImapConfig();
  const test = useImapTest();
  const toast = useToastHelpers();

  const [host, setHost] = useState<string>('');
  const [port, setPort] = useState<string>('');
  const [user, setUser] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [mailbox, setMailbox] = useState<string>('');
  const [interval, setIntervalMin] = useState<string>('');
  const [emailAddr, setEmailAddr] = useState<string>('');

  // Hydrate from server on first load.
  useSyncFormFromConfig(data, {
    setHost: (v) => setHost(v),
    setPort: (v) => setPort(v),
    setUser: (v) => setUser(v),
    setMailbox: (v) => setMailbox(v),
    setInterval: (v) => setIntervalMin(v),
    setEmailAddr: (v) => setEmailAddr(v),
  });

  if (isLoading) return <LoadingSpinner />;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    try {
      await patch.mutateAsync({
        imapHost: host.trim() || null,
        imapPort: port ? Number(port) : null,
        imapUser: user.trim() || null,
        // Empty password = don't touch; cleared explicitly would be
        // handled by a separate "Remove password" control. Keeping UX
        // simple: non-empty → replace, empty → leave encrypted value.
        imapPassword: password ? password : undefined,
        imapMailbox: mailbox.trim() || null,
        imapSyncIntervalMinutes: interval ? Number(interval) : null,
        imapEmailAddress: emailAddr.trim() || null,
      });
      setPassword('');
      toast.success('IMAP-Konfiguration gespeichert');
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function runTest() {
    try {
      const res = await test.mutateAsync();
      if (res.attempted) {
        toast.success(`IMAP-Test OK — ${res.imported} importiert`);
      } else {
        toast.info('IMAP-Test: keine Credentials hinterlegt');
      }
    } catch (err) {
      toast.error(`IMAP-Test fehlgeschlagen: ${(err as Error).message}`);
    }
  }

  const passwordSetAt = data?.imapPassword?.setAt;

  return (
    <form
      onSubmit={submit}
      data-testid="imap-config-form"
      className="rounded-lg border border-white/10 bg-white/5 p-5 space-y-4"
    >
      <header className="flex items-center justify-between">
        <div>
          <h3 className="text-lg text-white">IMAP-Ingress</h3>
          <p className="text-xs text-white/50">
            Eingehende Rechnungen aus der Mailbox abholen (FR-007).
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          onClick={runTest}
          disabled={test.isPending}
          data-testid="imap-test-button"
        >
          {test.isPending ? 'Teste…' : 'IMAP testen'}
        </Button>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input
          label="Host"
          value={host}
          onChange={(e) => setHost(e.target.value)}
          placeholder="mail.example.com"
          data-testid="imap-host"
        />
        <Input
          label="Port"
          type="number"
          value={port}
          onChange={(e) => setPort(e.target.value)}
          placeholder="993"
          data-testid="imap-port"
        />
        <Input
          label="Benutzer"
          value={user}
          onChange={(e) => setUser(e.target.value)}
          placeholder="rechnung@example.com"
          data-testid="imap-user"
        />
        <Input
          label="Passwort"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint={
            passwordSetAt
              ? `Hinterlegt am ${formatDateTimeBerlin(passwordSetAt)} — leer lassen, um es unverändert zu belassen.`
              : 'Noch kein Passwort gespeichert.'
          }
          placeholder={passwordSetAt ? '•••••••• (ändern)' : ''}
          data-testid="imap-password"
        />
        <Input
          label="Mailbox"
          value={mailbox}
          onChange={(e) => setMailbox(e.target.value)}
          placeholder="INBOX"
          data-testid="imap-mailbox"
        />
        <Input
          label="Sync-Intervall (Minuten)"
          type="number"
          value={interval}
          onChange={(e) => setIntervalMin(e.target.value)}
          placeholder="5"
          data-testid="imap-interval"
        />
        <Input
          label="Benachrichtigungsadresse"
          value={emailAddr}
          onChange={(e) => setEmailAddr(e.target.value)}
          placeholder="rechnungen@example.com"
          data-testid="imap-email-address"
          className="sm:col-span-2"
        />
      </div>

      <div className="flex items-center justify-end gap-2">
        <Button type="submit" disabled={patch.isPending} data-testid="imap-save">
          {patch.isPending ? 'Speichere…' : 'Speichern'}
        </Button>
      </div>
    </form>
  );
}

function DriveConfigForm() {
  const { data, isLoading } = useDriveConfig();
  const patch = usePatchDriveConfig();
  const test = useDriveTest();
  const toast = useToastHelpers();

  const [folderId, setFolderId] = useState('');
  const [sa, setSa] = useState('');

  useDriveSyncFromConfig(data, { setFolderId });

  if (isLoading) return <LoadingSpinner />;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    try {
      await patch.mutateAsync({
        driveFolderId: folderId.trim() || null,
        driveServiceAccountJson: sa ? sa : undefined,
      });
      setSa('');
      toast.success('Drive-Konfiguration gespeichert');
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function runTest() {
    try {
      const res = await test.mutateAsync();
      if (res.attempted) {
        toast.success(
          `Drive-Test OK — ${res.filesSeen} Dateien geprüft, ${res.imported} importiert`
        );
      } else {
        toast.info('Drive-Test: Service-Account nicht konfiguriert');
      }
    } catch (err) {
      toast.error(`Drive-Test fehlgeschlagen: ${(err as Error).message}`);
    }
  }

  const saSetAt = data?.driveServiceAccountJson?.setAt;

  return (
    <form
      onSubmit={submit}
      data-testid="drive-config-form"
      className="rounded-lg border border-white/10 bg-white/5 p-5 space-y-4"
    >
      <header className="flex items-center justify-between">
        <div>
          <h3 className="text-lg text-white">Google Drive-Ingress</h3>
          <p className="text-xs text-white/50">
            Neue Dateien aus einem Ordner abholen (FR-008).
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          onClick={runTest}
          disabled={test.isPending}
          data-testid="drive-test-button"
        >
          {test.isPending ? 'Teste…' : 'Drive testen'}
        </Button>
      </header>

      <Input
        label="Drive-Folder-ID"
        value={folderId}
        onChange={(e) => setFolderId(e.target.value)}
        placeholder="1abc…"
        data-testid="drive-folder-id"
      />

      <div>
        <label className="block text-sm font-medium text-white/70 mb-1.5">
          Service-Account JSON
        </label>
        <textarea
          value={sa}
          onChange={(e) => setSa(e.target.value)}
          rows={6}
          placeholder={
            saSetAt
              ? '{\n  …\n} (hinterlegt — leer lassen, um unverändert zu belassen)'
              : '{\n  "client_email": "...",\n  "private_key": "..."\n}'
          }
          data-testid="drive-service-account-json"
          className="w-full rounded border border-white/10 bg-black/30 p-3 text-xs font-mono text-white/80 focus:outline-none focus:ring-1 focus:ring-white/40"
        />
        {saSetAt && (
          <p className="text-xs text-white/50 mt-1">
            Hinterlegt am {formatDateTimeBerlin(saSetAt)}.
          </p>
        )}
      </div>

      <div className="flex items-center justify-end gap-2">
        <Button type="submit" disabled={patch.isPending} data-testid="drive-save">
          {patch.isPending ? 'Speichere…' : 'Speichern'}
        </Button>
      </div>
    </form>
  );
}

/**
 * One-shot hydrate helper. Guards against clobbering user edits: only
 * seeds the inputs while the local form state is still empty and after
 * the server has produced a non-null payload.
 */
function useSyncFormFromConfig(
  data: ReturnType<typeof useImapConfig>['data'],
  setters: {
    setHost: (v: string) => void;
    setPort: (v: string) => void;
    setUser: (v: string) => void;
    setMailbox: (v: string) => void;
    setInterval: (v: string) => void;
    setEmailAddr: (v: string) => void;
  }
) {
  const [hydrated, setHydrated] = useState(false);
  if (!hydrated && data) {
    setters.setHost(data.imapHost ?? '');
    setters.setPort(data.imapPort?.toString() ?? '');
    setters.setUser(data.imapUser ?? '');
    setters.setMailbox(data.imapMailbox ?? 'INBOX');
    setters.setInterval(data.imapSyncIntervalMinutes?.toString() ?? '5');
    setters.setEmailAddr(data.imapEmailAddress ?? '');
    setHydrated(true);
  }
}

function useDriveSyncFromConfig(
  data: ReturnType<typeof useDriveConfig>['data'],
  setters: { setFolderId: (v: string) => void }
) {
  const [hydrated, setHydrated] = useState(false);
  if (!hydrated && data) {
    setters.setFolderId(data.driveFolderId ?? '');
    setHydrated(true);
  }
}
